import { Link, createFileRoute } from '@tanstack/react-router';
import { AdminLayout } from '../components/AdminLayout.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/compliance')({
  component: AdminCompliance,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

function Tile({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p
          className={`text-3xl font-bold ${
            warn && value > 0 ? 'text-[oklch(0.6_0.2_25)]' : ''
          }`}
        >
          {value}
        </p>
        <p className="text-xs text-[var(--muted-foreground)] mt-1">{label}</p>
      </CardContent>
    </Card>
  );
}

function AdminCompliance() {
  const q = trpc.compliance.overview.useQuery();

  if (q.isLoading) {
    return (
      <AdminLayout>
        <p className="text-[var(--muted-foreground)]">Lade …</p>
      </AdminLayout>
    );
  }
  const snap = q.data;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="text-3xl font-bold">Compliance</h1>
          <p className="text-[var(--muted-foreground)]">
            AÜG-/MiLoG-Risiken + offene Vorgänge (Phase-1-Heuristiken)
          </p>
        </header>

        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <Tile label="Aufenthaltstitel kritisch" value={snap?.counts.permitExpiring ?? 0} warn />
          <Tile label="Einsätze > Höchstdauer" value={snap?.counts.longRunningAssignments ?? 0} warn />
          <Tile label="Worker-Reviews offen" value={snap?.counts.pendingWorkerReviews ?? 0} />
          <Tile label="Kunden-Reviews offen" value={snap?.counts.pendingClientReviews ?? 0} />
          <Tile label="Abwesenheits-Anträge" value={snap?.counts.pendingAbsences ?? 0} />
          <Tile label="Stundenzettel offen" value={snap?.counts.submittedTimesheets ?? 0} />
          <Tile label="Aktive Einsätze" value={snap?.counts.activeAssignments ?? 0} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Aufenthaltstitel — ablaufend / abgelaufen</CardTitle>
            <CardDescription>
              ≤ 60 Tage bis Ablauf oder bereits abgelaufen → Arbeitserlaubnis prüfen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {snap?.permitExpiring.length === 0 && (
              <p className="text-sm text-[var(--muted-foreground)]">Keine kritischen Titel.</p>
            )}
            {snap?.permitExpiring.map((w) => {
              const expired = (w.residencePermitExpiry ?? '') < today;
              return (
                <Link
                  key={w.id}
                  to="/admin/workers/$id"
                  params={{ id: w.id }}
                  className="flex items-center justify-between rounded-md border border-[var(--border)] p-2 text-sm hover:bg-[var(--accent)]"
                >
                  <span>{[w.firstName, w.lastName].filter(Boolean).join(' ') || w.id.slice(0, 8)}</span>
                  <span className={expired ? 'text-[oklch(0.6_0.2_25)] font-medium' : ''}>
                    {w.residencePermitExpiry}
                    {expired ? ' (abgelaufen)' : ''}
                  </span>
                </Link>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Einsätze über Überlassungs-Heuristik</CardTitle>
            <CardDescription>
              Aktive Einsätze mit Start vor &gt; ~9 Monaten (§8(4) AÜG — manuell prüfen)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {snap?.longRunningAssignments.length === 0 && (
              <p className="text-sm text-[var(--muted-foreground)]">Keine Auffälligkeiten.</p>
            )}
            {snap?.longRunningAssignments.map((a) => (
              <Link
                key={a.id}
                to="/admin/assignments/$id"
                params={{ id: a.id }}
                className="flex items-center justify-between rounded-md border border-[var(--border)] p-2 text-sm hover:bg-[var(--accent)]"
              >
                <span>
                  {[a.workerFirstName, a.workerLastName].filter(Boolean).join(' ') || '—'} →{' '}
                  {a.clientCompanyName ?? '—'}
                </span>
                <span className="text-[var(--muted-foreground)]">seit {a.startDate}</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
